// NotificationDetailPage — Single notification view
// Layout: hero w/ category, full body, action buttons, related

import { useNavigate, useParams } from 'react-router-dom';

const NOTIFICATIONS: Record<string, { type: 'trading' | 'bot' | 'challenge' | 'payout' | 'security' | 'news'; icon: string; color: string; title: string; body: string; time: string; actions: { label: string; to: string; primary?: boolean }[] }> = {
  'n1': {
    type: 'trading', icon: '📈', color: '#34C759',
    title: 'Bot closed NQ long +$420',
    body: 'Your AI Bot Trader closed a long position on NQ at 18,290.25. The trade lasted 2h 14m and beat the 1.9 risk:reward target. The bot is now scanning for the next setup on NQ, ES, and YM.\n\nThis was a Breakout strategy signal. The bot entered at 18,245.50 with a 25-point stop loss and 55-point take profit.',
    time: '2 min ago',
    actions: [
      { label: 'View trade', to: '/portal/trade/nq-895', primary: true },
      { label: 'View bot', to: '/portal/bot-config' },
    ],
  },
  'n2': {
    type: 'challenge', icon: '🎯', color: '#FF3B30',
    title: 'Challenge progress: 32% to target',
    body: 'You\'re $3,080 away from your $5,000 monthly profit goal. At your current pace (+$2,920 in 24 days), you\'ll hit the target in another 25 days. You\'ve used 38% of your $5,000 daily loss limit over the past 30 days — well within the 5% EOD drawdown cap.',
    time: '1 hour ago',
    actions: [
      { label: 'View challenge', to: '/portal/challenge/c1', primary: true },
      { label: 'View analytics', to: '/portal/analytics' },
    ],
  },
  'n3': {
    type: 'payout', icon: '💰', color: '#34C759',
    title: 'Payout of $1,240 sent to Chase Bank',
    body: 'Your weekly payout of $1,240.00 was sent to Chase Bank checking account ending in 4521. The transfer is expected to arrive within 1-2 business days. Transaction ID: TX-7C29A84F.',
    time: '3 hours ago',
    actions: [
      { label: 'View transaction', to: '/portal/payouts', primary: true },
      { label: 'View statement', to: '/portal/statements' },
    ],
  },
  'n4': {
    type: 'security', icon: '🛡️', color: '#FF9500',
    title: 'New device signed in',
    body: 'We noticed a sign-in from a new device:\n\niPhone 15 Pro · Safari 17\nSan Francisco, US · 203.0.113.42\n\nIf this was you, no action needed. If not, secure your account immediately.',
    time: 'Yesterday',
    actions: [
      { label: 'Review sessions', to: '/portal/security', primary: true },
      { label: 'It wasn\'t me', to: '/portal/security' },
    ],
  },
};

export function NotificationDetailPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const n = NOTIFICATIONS[id || 'n1'] || NOTIFICATIONS.n1;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Notification</div>
        <button onClick={() => alert('Notification options')} className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
        </button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4">
        <div className="rounded-[20px] border border-tf-border p-6 text-center" style={{ background: `linear-gradient(135deg, ${n.color} 0%, ${n.color}AA 100%)`, color: 'white' }}>
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[20px] text-[36px] bg-white/20" style={{ backdropFilter: 'blur(20px)' }}>{n.icon}</div>
          <div className="mt-3 text-[11px] uppercase tracking-wider text-white/80">{n.type}</div>
          <div className="mt-1 text-[20px] font-bold tracking-tight">{n.title}</div>
          <div className="mt-1 text-[12px] text-white/85">{n.time}</div>
        </div>
      </div>

      {/* Body */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Details</h2>
      <div className="mx-4 rounded-[16px] border border-tf-border bg-white p-4">
        <p className="text-[14px] leading-relaxed text-tf-text-1 whitespace-pre-line">{n.body}</p>
      </div>

      {/* Actions */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Actions</h2>
      <div className="mx-4 space-y-2">
        {n.actions.map((a) => (
          <button
            key={a.label}
            onClick={() => nav(a.to)}
            data-testid={`notif-action-${a.label}`}
            className={['flex h-12 w-full items-center justify-center rounded-full text-[15px] font-semibold transition',
              a.primary ? 'bg-tf-text-1 text-white active:scale-[0.98]' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* Settings shortcut */}
      <div className="mx-4 mt-3 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <button onClick={() => nav('/portal/notifications-settings')} className="flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition" data-testid="notif-settings">
          <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-purple-tint text-tf-info text-[18px]">⚙️</div>
          <div className="flex-1 text-left">
            <div className="text-[14px] font-semibold text-tf-text-1">Notification settings</div>
            <div className="text-[11px] text-tf-text-3">Manage per-channel preferences</div>
          </div>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
    </div>
  );
}
